/**
 * device_log — on-device diagnostic ring buffer.
 *
 * The Android WebView gives us no console access once the app ships, and the
 * recent production builds showed "no realtime + zero Sentry" with no way to
 * see what actually happened on the phone. This module records a bounded ring
 * of timestamped events — boot probe, captured console output, window errors,
 * and explicit instrumentation from ws/syncer/http/persist/lifecycle — that
 * survives app restarts via localStorage and can be exported from the /debug
 * screen.
 *
 * Deliberately dependency-free so ANY layer (transport, sync, auth, sentry)
 * can import it without import cycles. SSR-safe: without `window` the ring
 * still works in memory; persistence simply no-ops.
 */

export type DeviceLogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error';

export type DeviceLogEntry = {
	t: number;
	level: DeviceLogLevel;
	msg: string;
	data?: unknown;
};

const MAX_ENTRIES = 800;
const STORAGE_KEY = 'device_log_ring';
const MAX_STRING_LENGTH = 400;
const MAX_DEPTH = 4;

const is_browser = typeof window !== 'undefined';

let entries: DeviceLogEntry[] = [];
let booted = false;
let flush_timer: ReturnType<typeof setTimeout> | undefined;
// undefined = not resolved yet; null = no storage available
let storage: Storage | null | undefined;

function get_storage(): Storage | null {
	if (storage !== undefined) return storage;
	storage = is_browser && typeof localStorage !== 'undefined' ? localStorage : null;
	return storage;
}

/** Test hook: inject a fake Storage so persistence is assertable in node. */
export function device_log_set_storage_for_tests(value: Storage | null): void {
	storage = value;
}

function schedule_flush(): void {
	if (!get_storage() || flush_timer) return;
	flush_timer = setTimeout(() => {
		flush_timer = undefined;
		device_log_flush_now();
	}, 500);
}

/** Force-write the ring to storage (also used by pagehide/visibilitychange). */
export function device_log_flush_now(): void {
	const s = get_storage();
	if (!s) return;
	try {
		s.setItem(STORAGE_KEY, JSON.stringify(entries));
	} catch {
		// storage full / unavailable — the in-memory ring still works
	}
}

function is_valid_entry(value: unknown): value is DeviceLogEntry {
	if (typeof value !== 'object' || value === null) return false;
	const e = value as DeviceLogEntry;
	return typeof e.t === 'number' && typeof e.level === 'string' && typeof e.msg === 'string';
}

function load_persisted(): void {
	const s = get_storage();
	if (!s) return;
	try {
		const raw = s.getItem(STORAGE_KEY);
		if (!raw) return;
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed)) entries = parsed.filter(is_valid_entry).slice(-MAX_ENTRIES);
	} catch {
		// corrupted ring — start fresh
	}
}

const listeners = new Set<(entry: DeviceLogEntry) => void>();

/**
 * Record one ring entry. Data is stored raw (not pre-stringified) so the
 * /debug screen can render it, but consumers that need a string use
 * `device_log_stringify`.
 */
export function device_log_entry(level: DeviceLogLevel, msg: string, data?: unknown): void {
	entries.push({ t: Date.now(), level, msg, data: data === undefined ? undefined : data });
	if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
	const latest = entries[entries.length - 1];
	for (const fn of listeners) fn(latest);
	schedule_flush();
}

/** Subscribe to new entries (for the /debug live tail). Returns unsubscribe. */
export function device_log_subscribe(cb: (entry: DeviceLogEntry) => void): () => void {
	listeners.add(cb);
	return () => {
		listeners.delete(cb);
	};
}

export function device_log_get(): DeviceLogEntry[] {
	return entries.slice();
}

/** Compact human-readable tail — used by the Sentry attach point. */
export function device_log_tail(n: number): string[] {
	return entries
		.slice(-n)
		.map(
			(e) =>
				`[${new Date(e.t).toISOString()}] ${e.level.toUpperCase()}: ${e.msg}${e.data === undefined ? '' : ' ' + device_log_stringify(e.data)}`
		);
}

export function device_log_export(): string {
	return JSON.stringify(entries, null, 2);
}

export function device_log_clear(): void {
	entries = [];
	const s = get_storage();
	try {
		s?.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}

export function device_log_count(): number {
	return entries.length;
}

/**
 * Safe stringifier: handles circular references (via depth cap), Errors,
 * Dates, and Event-like objects (a raw WebSocket Event serializes as a
 * useless `{}` — describe what matters instead).
 */
export function device_log_stringify(value: unknown, depth = 0): string {
	if (depth > MAX_DEPTH) return '[DepthLimit]';
	if (value === null) return 'null';
	if (typeof value === 'undefined') return 'undefined';
	if (typeof value === 'string') {
		return value.length > MAX_STRING_LENGTH ? value.slice(0, MAX_STRING_LENGTH) + '…' : value;
	}
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	if (typeof value === 'bigint') return String(value) + 'n';
	if (typeof value === 'symbol') return String(value);
	if (typeof value === 'function') return `[Function ${(value as Function).name || 'anonymous'}]`;
	if (value instanceof Error) {
		const name = value.name && value.name !== 'Error' ? ` [${value.name}]` : '';
		return `Error${name}: ${value.message}`;
	}
	if (value instanceof Date) return value.toISOString();

	// Everything below is an object.
	const obj = value as Record<string, unknown>;
	// Event-like: has a `type` plus isTrusted/target (or a real *Event ctor).
	const ctor_name = (value as { constructor?: { name?: string } }).constructor?.name ?? '';
	if (
		typeof obj.type === 'string' &&
		(obj.isTrusted !== undefined || obj.target !== undefined || /Event$/.test(ctor_name))
	) {
		const event_name = /Event$/.test(ctor_name) ? ctor_name : 'Event';
		return `[${event_name} type=${String(obj.type)} isTrusted=${String(obj.isTrusted)}]`;
	}
	if (Array.isArray(value)) {
		if (value.length === 0) return '[]';
		const parts = value.slice(0, 8).map((v) => device_log_stringify(v, depth + 1));
		if (value.length > 8) parts.push(`…+${value.length - 8}`);
		return `[${parts.join(', ')}]`;
	}
	const keys = Object.keys(obj);
	if (keys.length === 0) return '{}';
	const parts = keys.slice(0, 8).map((k) => `${k}: ${device_log_stringify(obj[k], depth + 1)}`);
	if (keys.length > 8) parts.push(`…+${keys.length - 8}`);
	return `{${parts.join(', ')}}`;
}

function capture_console(): void {
	if (typeof console === 'undefined') return;
	const original = {
		log: console.log.bind(console),
		debug: console.debug.bind(console),
		info: console.info.bind(console),
		warn: console.warn.bind(console),
		error: console.error.bind(console)
	};
	const record =
		(level: DeviceLogLevel) =>
		(...args: unknown[]) => {
			const msg = args.map((a) => (typeof a === 'string' ? a : device_log_stringify(a))).join(' ');
			device_log_entry(level, msg);
			original[level](...args);
		};
	console.log = record('log');
	console.debug = record('debug');
	console.info = record('info');
	console.warn = record('warn');
	console.error = record('error');
}

function capture_window_errors(): void {
	if (!is_browser) return;
	window.addEventListener('error', (event) => {
		device_log_entry('error', 'window.error', {
			message: event.message,
			source: event.filename,
			line: event.lineno,
			col: event.colno
		});
	});
	window.addEventListener('unhandledrejection', (event) => {
		device_log_entry('error', 'window.unhandledrejection', {
			reason: device_log_stringify(event.reason)
		});
	});
	// Persist the ring when the app goes away so the NEXT boot sees it.
	window.addEventListener('pagehide', () => device_log_flush_now());
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') device_log_flush_now();
	});
}

function write_boot_probe(): void {
	if (booted) return;
	booted = true;
	device_log_entry('info', 'device_log.boot', {
		env: import.meta.env.MODE ?? 'production',
		href: is_browser ? window.location.href : '',
		user_agent: is_browser ? navigator.userAgent : '',
		at: new Date().toISOString()
	});
}

// Module init: restore the previous run's tail, mark the new session, then
// capture console + window errors from here on.
load_persisted();
write_boot_probe();
capture_console();
capture_window_errors();

/** Test isolation: drop the ring, un-resolve storage, allow a fresh boot probe. */
export function reset_device_log_for_tests(): void {
	booted = false;
	entries = [];
	storage = undefined;
	if (flush_timer) {
		clearTimeout(flush_timer);
		flush_timer = undefined;
	}
}

/** Test hook: re-run the module-init persistence load (simulates an app restart). */
export function device_log_reload_for_tests(): void {
	entries = [];
	load_persisted();
}
